import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { operations } from "redux/Navigation";
import excel from "../../../../assets/images/Excel.svg";
import { useDebounce } from "client/CommonComponents/CustomDebounce/DebounceHook";
import AssetAuditResetHistoryLog from "./AssetAuditResetHistoryLog";
import { base64ToArrayBuffer, saveByteArray } from "utils";
import {
  getAssetAuditHistoryExcelData,
  getAssetAuditHistoryLogGridData,
  setAssetAuditHistoryExcelData,
  setAssetAuditHistoryLogGridData,
} from "Store/AssetInventory/AssetAuditResetHistoryLog/AssetAuditResetHistoryLogAction";
import userProfileSelectors from "../../../../../redux/UserProfile/selectors";
import permissions, { isAuthorized } from "client/utils/Permissions";
import { EventHandler } from "../../../../CommonComponents";

const AssetAuditResetHistoryLogContainer = () => {
  const Navigate = useNavigate();
  const dispatch = useDispatch();
  const navDispatch = operations.dispatchNav(dispatch);
  const dispatchHeaderNav = operations.dispatchHeaderNav(dispatch);

  const [paginationData, setPaginationData] = useState<any>({});
  const [sortData, setSortData] = useState<any>({});
  const [serverTotal, setServerTotal] = useState(0);
  const [pagesCount, setPagesCount] = useState<number>(0);
  const [showGrid, setShowGrid] = useState<any>(false);

  const {
    assetAuditResetHistoryLogGridData,
    assetAuditResetHistoryLogExcelData,
    isGridLoading,
    userDetails,
  }: any = useSelector((state: any) => ({
    assetAuditResetHistoryLogGridData:
      state.AssetAuditResetHistoryLogReducer?.assetAuditResetHistoryLogGridData,
    assetAuditResetHistoryLogExcelData:
      state.AssetAuditResetHistoryLogReducer
        ?.assetAuditResetHistoryLogExcelData,
    isGridLoading: state.AssetAuditResetHistoryLogReducer?.isGridLoading,
    userDetails: userProfileSelectors.getUserDetails(state),
  }));

  useEffect(() => {
    if(!isAuthorized(permissions.ViewAdmin) && userDetails?.emailId){
      Navigate('/ordermanagement/blank');
    }
  },[])

  const onClickDownloadResult = (e?: any, resetId?: any) => {
    e?.preventDefault();
    if (resetId) {
      dispatch(getAssetAuditHistoryExcelData(resetId));
    }
  };

  useEffect(() => {
    if (assetAuditResetHistoryLogExcelData?.length) {
      const bytes = base64ToArrayBuffer(assetAuditResetHistoryLogExcelData);
      saveByteArray(
        "AssetAuditHistory",
        bytes,
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      );
      dispatch(setAssetAuditHistoryExcelData([]));
    }
  }, [assetAuditResetHistoryLogExcelData]);

  const columns = [
    {
      key: "resetAuditId",
      title: "Download Results",
      width: 120,
      isSortable: false,
      Cell: (row: any) => {
        return (
          <div className="outerBlock">
            {row.rowData.status === "Completed" && (
              <div className="excelBlock">
         
                                <EventHandler onEvent={(e: any) => onClickDownloadResult(e, row?.rowData?.resetAuditId)} data-attribute="AssetAuditResetHistoryLog_container_Default_EventHandler">
                                  <img
                                    src={excel}
                                    alt="excel"
                                  />
                                </EventHandler>
                
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "resetDate",
      title: "Date of Reset (PST)",
      width: 120,
      Cell: (row: any) => {
        return (
          <p className="dateSubmitted">
            {row.rowData.resetDate
              ? moment(row?.rowData?.resetDate).format("MM/DD/YYYY HH:mm A")
              : ""}
          </p>
        );
      },
    },
    {
      key: "resetBy",
      title: "Reset By",
      width: 120,
    },
    {
      key: "status",
      title: "Status",
      width: 120
    },
    {
      key: "comments",
      title: "Comments",
      width: 120,
      isSortable: false,
    }
  ];

  const fetchAction = useCallback((fetchData: any) => {
    if (fetchData.action && fetchData.action === "paging") {
      setPaginationData(fetchData.data);
    }
    if (fetchData.action && fetchData.action === "sorting") {
      setSortData(fetchData.data[0]);
    }
  }, []);

  const getGridData = () => {
    let payload: any = {
      pageNumber:
        paginationData?.pageIndex != undefined
          ? paginationData?.pageIndex + 1
          : undefined,
      pageSize: paginationData?.pageSize,
    };

    if (sortData?.key != "") {
      payload.sortBy = sortData?.key;
      payload.sortOrder = sortData?.sortDirection;
    }
    return payload;
  };

  useEffect(() => {
    if (serverTotal && paginationData?.pageSize) {
      let pageSize = paginationData?.pageSize;
      let tempPageCount =
        serverTotal > pageSize ? Math.ceil(serverTotal / pageSize - 1) : 0;
      setPagesCount(tempPageCount);
    }
  }, [serverTotal, paginationData?.pageSize]);

  const debounceObject = [paginationData, sortData];

  const globalDebounce = useDebounce(debounceObject, 500);

  useEffect(() => {
    const payload = getGridData();
    if (
      payload.pageNumber &&
      payload.pageSize &&
      userDetails.hasOwnProperty("permissions")
    ) {
      dispatch(getAssetAuditHistoryLogGridData(payload));
    }
  }, [globalDebounce, userDetails?.permissions]);

  useEffect(() => {
    if (assetAuditResetHistoryLogGridData?.data?.length) {
      setServerTotal(assetAuditResetHistoryLogGridData?.totalRecords);
    }
  }, [assetAuditResetHistoryLogGridData]);

  const breadcrumlist = [
    {
      text: "Admin Settings",
      key: "f1",
      href: "/ordermanagement",
      onClick: (e: any) => {
        e.preventDefault();
        Navigate("/ordermanagement/Admin/settings");
        navDispatch("Admin");
        dispatchHeaderNav("Admin");
      },
    },
    { text: "Asset Audit History", key: "f2" },
  ];

  const handleClickBack = () => {
    Navigate("/ordermanagement");
    dispatch(setAssetAuditHistoryLogGridData([]));
  };

  useEffect(() => {
    if (userDetails.hasOwnProperty("permissions")) {
      if (isAuthorized("View_Admin")) {
        setShowGrid(true);
        navDispatch("Admin");
        dispatchHeaderNav("Admin");
      } else {
        Navigate("/ordermanagement/blank");
      }
    }
  }, [userDetails]);

  return (
    <AssetAuditResetHistoryLog
      breadcrumlist={breadcrumlist}
      handleClickBack={handleClickBack}
      columns={columns}
      gridData={assetAuditResetHistoryLogGridData?.data}
      serverTotal={serverTotal}
      fetchAction={fetchAction}
      pagesCount={pagesCount}
      isGridLoading={isGridLoading}
      showGrid={showGrid}
    />
  );
};
export default AssetAuditResetHistoryLogContainer;
